import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Center,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Text
} from '@chakra-ui/react'

import {
  getVerifiedServices,
  getMyCommunityRatings
} from '../lib/api-helper'

import Page from '../components/page'
import Background from '../components/background'
import VerifiedServiceCard from '../components/verified-services/card'
import RatingModal from '../components/verified-services/rating-modal'
import { useRanContext } from '../components/ran-state-provider'

const VerifiedServices = () => {
  const navigate = useNavigate()
  const { isAuthenticated, setAuthentication, setToast } = useRanContext()

  const [isLoading, setLoading] = useState(true)
  const [groups, setGroups] = useState([])
  const [activeType, setActiveType] = useState(null)

  // Ratings this player has already left, keyed by member id.
  const [myRatings, setMyRatings] = useState({})
  const [ratingMember, setRatingMember] = useState(null)

  useEffect(() => {
    async function fetchData() {
      await fetchInitialData()
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      setMyRatings({})
      return
    }

    async function fetchRatings() {
      await fetchMyRatings()
    }

    fetchRatings()
  }, [isAuthenticated])

  const fetchInitialData = async () => {
    setLoading(true)

    const result = await getVerifiedServices()

    if (result?.success) {
      setGroups(result.data || [])
    }

    setLoading(false)
  }

  const fetchMyRatings = async () => {
    const result = await getMyCommunityRatings(setAuthentication)

    if (result?.success) {
      const map = {}
      ;(result.data || []).forEach(rating => {
        map[rating.communityMemberId] = rating
      })
      setMyRatings(map)
    }
  }

  // Rating needs an account. Redirect imperatively - rendering a <Navigate>
  // from the modal loops forever because AnimatePresence keeps this page mounted
  // while it animates out.
  const onRate = member => {
    if (!isAuthenticated) {
      setToast({
        status: 'error',
        messages: ['Please log in to rate a verified member.']
      })
      navigate('/login')
      return
    }

    setRatingMember(member)
  }

  // Fold the saved rating back into the card without refetching the whole list.
  const onRated = data => {
    if (!data) return

    setMyRatings(current => ({
      ...current,
      [data.communityMemberId]: {
        communityMemberId: data.communityMemberId,
        score: data.score,
        review: data.review
      }
    }))

    setGroups(current =>
      current.map(group => ({
        ...group,
        members: group.members.map(member =>
          member.id === data.communityMemberId
            ? {
                ...member,
                averageScore: data.averageScore,
                ratingCount: data.ratingCount
              }
            : member
        )
      }))
    )
  }

  const visibleGroups = groups.filter(group => group.count > 0)

  const activeGroup =
    visibleGroups.find(group => group.type === activeType) || visibleGroups[0]

  const VerifiedServicesHeader = () => (
    <Heading variant="main" fontSize={[32]} fontWeight={800} mb={5}>
      VERIFIED SERVICES
    </Heading>
  )

  return (
    <>
      <Page
        title="Verified Services"
        header={<VerifiedServicesHeader />}
        highProfile
      >
        <Box boxSize="full">
          <Text fontSize={14} mb={4}>
            Official verified pilots, middlemen, boosters and content creators.
            For your safety, only transact with verified members listed here.
          </Text>

          {isLoading ? (
            <Center>
              <Spinner />
            </Center>
          ) : !visibleGroups.length ? (
            <Text fontSize={14} opacity={0.6}>
              No verified services have been listed yet.
            </Text>
          ) : (
            <>
              <Flex
                borderBottom="1px solid"
                borderColor="light-gray"
                mb={5}
                flexWrap="wrap"
              >
                {visibleGroups.map(group => {
                  const isActive = activeGroup?.type === group.type

                  return (
                    <Box
                      as="button"
                      type="button"
                      key={`verified-tab-${group.type}`}
                      onClick={() => setActiveType(group.type)}
                      px={4}
                      py={2}
                      mb="-1px"
                      fontSize={13}
                      letterSpacing={1}
                      fontWeight={isActive ? 700 : 400}
                      opacity={isActive ? 1 : 0.5}
                      borderBottom="2px solid"
                      borderColor={isActive ? 'maroon' : 'transparent'}
                      _hover={{ opacity: 1 }}
                    >
                      {group.typeName.toUpperCase()} ({group.count})
                    </Box>
                  )
                })}
              </Flex>

              <SimpleGrid minChildWidth={300} spacing={5}>
                {activeGroup?.members.map(member => (
                  <VerifiedServiceCard
                    key={`verified-member-${member.id}`}
                    member={member}
                    myRating={myRatings[member.id]}
                    onRate={onRate}
                  />
                ))}
              </SimpleGrid>
            </>
          )}
        </Box>
      </Page>

      <Background highProfile />

      {ratingMember && (
        <RatingModal
          member={ratingMember}
          myRating={myRatings[ratingMember.id]}
          onClose={() => setRatingMember(null)}
          onRated={onRated}
          setToast={setToast}
        />
      )}
    </>
  )
}

export default VerifiedServices
